import { getContextDeep } from "@builder/store/slices/BuilderSlice/builderHelpers";
import { getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateDynamicDataType } from "@builder/store/slices/BuilderSlice/builderSlice";
import { setActionDynamicData, setCurrentPopup } from "@builder/store/slices/popupSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import TextInput from "../TextInput";
import "./style.scss";
import { DynamicDataContext } from "@common/build/activeEntityType";
import classNames from "classnames";

const DynamicDataSelectorInput = ({
    disabled,
    title,
    actionDynamicDataType,
    actionDynamicDataId,
    hideTitle,
    customClassName
}: {
    disabled?: boolean;
    title?: string;
    actionDynamicDataType?: DynamicDataContext | null;
    actionDynamicDataId?: string | null;
    hideTitle?: boolean;
    customClassName?: string;
}) => {
    const dispatch = useAppDispatch();
    const selected = useAppSelector(getSelectedNode);
    const { t } = useTranslation();
    const builderState = useAppSelector(state => state.builder);

    const buildSettingsKey = useAppSelector(state => state.buildSettingsKey);

    const dataType = actionDynamicDataType || getContextDeep(builderState, selected?.id) || "general";
    const actionDynamicData = useAppSelector(state =>
        actionDynamicDataId ? state.popup.dynamicDataFields[actionDynamicDataId]?.actionDynamicData : null
    );
    const selectedDynamicData = actionDynamicData || selected?.dynamicData || false;
    const icon = selectedDynamicData ? "/x-icon-small-bright.svg" : "/db-icon.svg";
    const icon2 = selectedDynamicData ? "/db-icon2.svg" : "";

    const [inputPlaceholder, setInputPlaceholder] = useState<string>("");

    const className = classNames("dynamic-data-selector-has-data");

    const handleRemoveDynamicData = () => {
        if (actionDynamicDataType && actionDynamicDataId) {
            dispatch(setActionDynamicData({ id: actionDynamicDataId, data: null }));
            return;
        }
        setTimeout(() => dispatch(updateDynamicDataType()), 0);
    };

    useEffect(() => {
        if (selected?.dynamicData || actionDynamicData) {
            if (selected?.dynamicData && buildSettingsKey?.[selected.dynamicData]?.label) {
                setInputPlaceholder(buildSettingsKey[selected.dynamicData].label || "");
            } else {
                const dynamicData = actionDynamicDataType ? actionDynamicData : selected?.dynamicData;
                if (dynamicData) {
                    const label = `${t(`popup.popupSidebar.dynamicData.${dataType}`)}: ${t(`dynamicDataString.${dynamicData}`)}`;
                    setInputPlaceholder(label);
                }
            }
        } else {
            setInputPlaceholder("");
        }
    }, [selected?.dynamicData, buildSettingsKey, actionDynamicData, actionDynamicDataType, dataType, t]);

    return (
        <TextInput
            onClick={() => {
                if (!selectedDynamicData && disabled) return;
                dispatch(setCurrentPopup("dynamicData"));
            }}
            title={hideTitle ? "" : title || "Dynamic data"}
            placeholder="No Data Selected"
            disabled={disabled}
            noTranslate={true}
            className={classNames({
                [className]: !!inputPlaceholder,
                [customClassName || ""]: !!customClassName
            })}
            value={inputPlaceholder}
            callback={(value: string) => setInputPlaceholder(value)}
            readOnly={true}
            icon={{
                src: icon,
                callback: (event: React.MouseEvent) => {
                    event.stopPropagation();
                    event.preventDefault();
                    event.stopPropagation();
                    if (selectedDynamicData) {
                        handleRemoveDynamicData();
                    } else {
                        dispatch(setCurrentPopup("dynamicData"));
                    }
                }
            }}
            icon2={{
                src: icon2,
                callback: (event: React.MouseEvent) => {
                    if (disabled) return;

                    event.preventDefault();
                    if (selectedDynamicData) {
                        dispatch(setCurrentPopup("dynamicData"));
                    }
                }
            }}
        />
    );
};

export default DynamicDataSelectorInput;
